import { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import ConfirmModal from './ConfirmModal';
import { Pencil1Icon, DotsVerticalIcon, EyeOpenIcon, CheckIcon } from '@radix-ui/react-icons';
import AppContext from '../contexts/AppContext';

function Chevron({ open }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className={`ml-2 h-3 w-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
    >
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11l3.71-3.77a.75.75 0 1 1 1.08 1.04l-4.24 4.3a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z" fill="currentColor" />
    </svg>
  );
}

function TemplatesPopover({
  availableActions = [],
  templates = [],
  loading,
  error,
  onSelect,
  assigningTemplateId,
  assignError
}) {
  const filteredTemplates = templates.filter(t => availableActions.includes(t.id));

  if (loading) {
    return (
      <div className="min-w-[260px] max-h-72 overflow-y-auto rounded border border-black/10 bg-white p-2 shadow-xl">
        <div className="px-2 py-1 text-xs font-semibold text-gray-600">Chargement…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-w-[260px] max-h-72 overflow-y-auto rounded border border-black/10 bg-white p-2 shadow-xl">
        <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">Erreur: {error}</div>
      </div>
    );
  }
  if (!filteredTemplates?.length) {
    return (
      <div className="min-w-[260px] max-h-72 overflow-y-auto rounded border border-black/10 bg-white p-2 shadow-xl">
        <div className="px-2 py-1 text-xs font-semibold text-gray-600">Aucune action disponible</div>
      </div>
    );
  }
  return (
    <div className="min-w-[260px] max-h-72 overflow-y-auto rounded border border-black/10 bg-white p-2 shadow-xl">
      <div className="px-2 py-1 text-xs font-semibold text-gray-600">Actions disponibles</div>
      {assignError ? (
        <div className="mb-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">Impossible d'ajouter l'action: {assignError}</div>
      ) : null}
      <ul className="list-none m-0 p-0">
        {filteredTemplates.map(t => {
          const isAssigning = assigningTemplateId === t.id;

          // hide [tech] templates they cannot be chosen in the interface
          // they are automatically assigned to the issue by Jira
          if (t.name.toLowerCase().includes('[tech]'))
            return null;

          return (
            <li
              key={t.id}
              onClick={() => onSelect(t)}
              className={`flex cursor-pointer items-center justify-between rounded px-2 py-2 hover:bg-gray-50 ${isAssigning ? 'pointer-events-none opacity-60' : ''}`}
            >
              <div className="text-sm text-gray-900">{t.name}</div>
              {isAssigning ? (
                <div className="text-xs text-gray-600">Ajout…</div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ActionsMenuPopover({
  onDelete,
  onDuplicate,
  isOpen
}) {
  if (!isOpen) return null;

  return (
    <div className="min-w-[140px] rounded border border-black/10 bg-white p-1 shadow-xl">
      <button
        type="button"
        onClick={onDuplicate}
        className="flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <span>Dupliquer</span>
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
      >
        <span>Supprimer</span>
      </button>
    </div>
  );
}

const getSubTitle = (form) => {
  if (!form?.design?.questions || !form?.state?.answers || form.submitted !== true) return null;

  let marque = null;
  let meuble = null;

  Object.entries(form.design.questions).forEach(([qId, question]) => {
    if (question.jiraField === 'customfield_10627') { // Marque
      const answer = form.state.answers[qId];
      if (answer?.choices?.[0]) {
        const choice = question.choices?.find(c => c.id === answer.choices[0]);
        if (choice) marque = choice.label;
      }
    }

    if (question.jiraField === 'customfield_10629') { // Mobilier
      const answer = form.state.answers[qId];
      if (answer?.choices?.[0]) {
        const choice = question.choices?.find(c => c.id === answer.choices[0]);
        if (choice) meuble = choice.label;
      }
    }
  });

  // if (marque && meuble) return `${marque} - ${meuble}`;
  if (meuble) return meuble;
  if (marque) return marque;
  return null;
};

export default function IssuesActions({ issue, onFormsUpdated = () => {} }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [assigningTemplateId, setAssigningTemplateId] = useState(null);
  const [assignError, setAssignError] = useState('');
  const [attachedForms, setAttachedForms] = useState([]);
  const [attachedLoading, setAttachedLoading] = useState(false);
  const [attachedError, setAttachedError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const containerRef = useRef(null);
  const { serviceDesk, currentUser } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config;
  const availableActions = serviceDeskConfig?.availableActions?.[issue.mappedFields?.requestType?.requestType?.id] || [];
  const filteredTemplates = templates.filter(t => availableActions.includes(t.id));
  const isTechnician = currentUser?.role === 'technician';

  const handleFormsUpdated = (forms) => {
    setAttachedForms(forms);
    onFormsUpdated(forms);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setOpenMenuId(null);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadTemplates() {
      setLoading(true);
      setError('');
      try {
        // Pass issueKey to help resolve service desk for technicians with wildcard access
        const issueKey = issue?.key || issue?.issueKey;
        const url = issueKey ? `/forms-templates?issueKey=${encodeURIComponent(issueKey)}` : '/forms-templates';
        const res = await fetchWithAuth(url);
        const body = await res.json();
        if (!res.ok || !body?.success) {
          throw new Error(body?.error || `HTTP ${res.status}`);
        }
        if (!cancelled) setTemplates(body.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur inconnue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    async function loadAttached() {
      setAttachedLoading(true);
      setAttachedError('');
      try {
        // 1. Fast load (light=true) to show the list immediately
        const resLight = await fetchWithAuth(`/issues/${encodeURIComponent(issue.id)}/forms?light=true`);
        const bodyLight = await resLight.json();

        if (!resLight.ok || bodyLight?.success === false) {
          throw new Error(bodyLight?.error || `HTTP ${resLight.status}`);
        }

        if (!cancelled) {
          handleFormsUpdated(bodyLight?.data || []);
          setAttachedLoading(false); // List is visible
        }

        // 2. Background load (full details) to populate subtitles
        const resFull = await fetchWithAuth(`/issues/${encodeURIComponent(issue.id)}/forms`);
        const bodyFull = await resFull.json();

        if (!cancelled && resFull.ok && bodyFull?.success !== false) {
          handleFormsUpdated(bodyFull?.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          // If light load failed, we show error.
          // If full load failed but light succeeded, we might want to keep the list but show a toast?
          // For now, if error occurs in the first phase or second phase (and we haven't handled it gracefully), show error.
          // Actually if step 1 succeeds, attachedLoading is false. If step 2 fails, we might just log it or show a discreet error?
          // The current error handling sets global error.
          if (attachedLoading) {
             setAttachedError(err?.message || 'Erreur inconnue');
             setAttachedLoading(false);
          } else {
             console.error("Background forms load failed", err);
          }
        }
      }
    }

    // run requests in parallel
    loadTemplates();
    loadAttached();

    return () => { cancelled = true; };
  }, [issue?.issueId]);

  const reloadAttachedForms = async () => {
    try {
      setAttachedLoading(true);
      const res = await fetchWithAuth(`/issues/${issue.id}/forms`);
      const body = await res.json();
      if (res.ok && body?.success !== false) {
        handleFormsUpdated(body?.data || []);
      }
    } catch (e) {
      // ignore on silent refresh
    } finally {
      setAttachedLoading(false);
    }
  };

  const onSelectTemplate = async (tpl) => {
    setAssignError('');
    setAssigningTemplateId(tpl.id);
    try {
      const res = await fetchWithAuth(`/issues/${issue.id}/actions/${tpl.id}`, {
        method: 'POST'
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      setOpen(false);
      // refresh attached forms list
      reloadAttachedForms();
    } catch (err) {
      setAssignError(err?.message || 'Erreur inconnue');
    } finally {
      setAssigningTemplateId(null);
    }
  };

  const onViewForm = (form) => {
    navigate(`/issues/${encodeURIComponent(issue.key)}/forms/${encodeURIComponent(form.id)}`, {
      state: {
        issueId: issue.issueId,
        issueKey: issue.key,
        issue: issue
      }
    });
  };

  const onRequestDelete = (form) => {
    setDeleteError('');
    setDeletingId(form.id);
    setConfirmOpen(true);
    setOpenMenuId(null);
  };

  const onDuplicateForm = async (form) => {
    setOpenMenuId(null);
    try {
      setAttachedLoading(true);
      const res = await fetchWithAuth(`/issues/${issue.id}/forms/${form.id}/duplicate`, {
        method: 'POST'
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      await reloadAttachedForms();
    } catch (err) {
      setAttachedError(err?.message || 'Erreur de duplication');
    } finally {
      setAttachedLoading(false);
    }
  };

  const toggleMenu = (formId) => {
    setOpenMenuId(openMenuId === formId ? null : formId);
  };

  const onConfirmDelete = async () => {
    if (!deletingId) return;
    setDeleteError('');
    try {
      const res = await fetchWithAuth(`/issues/${issue.id}/forms/${deletingId}`, {
        method: 'DELETE'
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      setConfirmOpen(false);
      setDeletingId(null);
      await reloadAttachedForms();
    } catch (err) {
      setDeleteError(err?.message || 'Erreur inconnue');
    }
  };

  const isIssueOpen = parseInt(issue?.fields?.status?.id) === serviceDeskConfig?.issueValidation?.statusIdOpen;

  return (
    <div ref={containerRef} className="relative w-full">

      <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2 flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Actions {attachedLoading ? '' : `(${attachedForms.length})`}
      </h3>

      <div className="mb-2 w-full rounded border border-black/10 bg-white p-2">
        {attachedLoading ? (
          <div className="px-2 py-1 text-xs text-gray-500">Chargement…</div>
        ) : attachedError ? (
          <div className="mx-2 mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">Erreur: {attachedError}</div>
        ) : (
          <ul className="m-0 list-none p-0 w-full">
            {(attachedForms || []).length === 0 ? (
              <li className="flex w-full items-center rounded px-2 py-2 text-gray-600">Aucune action</li>
            ) : (
              attachedForms.sort((a, b) => new Date(a.updated) - new Date(b.updated)).map(f => {
                const subTitle = getSubTitle(f);
                const normalizedName = (f.name || f.title || '').toLowerCase();
                const isTechForm = normalizedName.includes('[tech]');
                const canTechnicianEdit = isTechnician && isTechForm && !f.submitted;
                const canEditForm = (isIssueOpen && !f.submitted) || canTechnicianEdit;
                const isLoadingDetails = f.submitted && (!f.design || !f.state);

                // Clean title by removing [TECH] or [tech] tags
                const cleanTitle = (f.name || f.title || f.id).replace(/\[tech\]/gi, '').trim();

                return <li key={f.id} className="flex w-full items-center rounded px-2 py-2 hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <div className="text-sm text-gray-900 flex items-center gap-2">
                      {isTechForm && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          technicien
                        </span>
                      )}
                      {cleanTitle}
                    </div>
                    {!isTechForm && (
                      isLoadingDetails ? (
                        <div className="mt-1 h-3 w-24 animate-pulse rounded bg-gray-200" />
                      ) : subTitle && (
                        <div className="text-xs text-gray-600">
                          {subTitle}
                        </div>
                      )
                    )}
                  </div>

                  <div className="ml-auto flex items-center gap-2">
                    {(() => {
                      const Icon = !f.submitted && canEditForm ? Pencil1Icon : EyeOpenIcon;
                      const title = !f.submitted && canEditForm ? 'Compléter' : 'Voir';

                      return (
                        <div className="flex items-center gap-1.5">
                          {f.submitted && (
                            <CheckIcon className="w-4 h-4 text-green-600" title="Soumis" />
                          )}
                          <button
                            type="button"
                            onClick={() => onViewForm(f)}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                            title={title}
                          >
                            <Icon className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })()}

                    {isIssueOpen && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleMenu(f.id)}
                          className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                          title="Actions"
                        >
                          <DotsVerticalIcon className="w-4 h-4" />
                        </button>
                        {openMenuId === f.id && (
                          <div className="absolute right-0 top-full mt-1 z-[1000]">
                            <ActionsMenuPopover
                              onDelete={() => onRequestDelete(f)}
                              onDuplicate={() => onDuplicateForm(f)}
                              isOpen={true}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              })
            )}
          </ul>
        )}
      </div>

      {isIssueOpen && (
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className={`inline-flex items-center rounded border border-gray-300 bg-gray-100 px-2.5 py-1.5 text-sm text-gray-800 hover:bg-gray-200 ${!isIssueOpen ? 'line-through' : ''}`}
            disabled={!isIssueOpen}
        >
          Ajouter une action
          <Chevron open={open} />
        </button>
      )}

      {open && (
        <div className="absolute left-0 z-[1000] mt-1 z-[1000]">
          <TemplatesPopover
            availableActions={availableActions}
            templates={templates}
            loading={loading}
            error={error}
            onSelect={onSelectTemplate}
            assigningTemplateId={assigningTemplateId}
            assignError={assignError}
          />
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Supprimer l'action attachée ?"
        description="Cette action retirera définitivement le formulaire de ce ticket."
        confirmText="Supprimer"
        cancelText="Annuler"
        onConfirm={onConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeletingId(null); setDeleteError(''); }}
        loading={false}
        error={deleteError}
      />
    </div>
  );
}
